import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import translations from "../locales/translations";
import {
  FaServer,
  FaCode,
  FaSitemap,
  FaGlobe,
  FaDatabase,
  FaMicrochip,
  FaTimes,
  FaLock,
  FaChevronRight,
  FaCheckCircle,
} from "react-icons/fa";
import "./Projects.css";

const pageVariants = {
  initial: { opacity: 0, y: 30 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -30 },
};

const ICONS = {
  si: FaServer,
  gl: FaCode,
  ro: FaSitemap,
  web: FaGlobe,
  bd: FaDatabase,
  iot: FaMicrochip,
};

const projects = [
  {
    id: 1,
    iconKey: "si",
    title: {
      fr: "Systèmes d'Information",
      en: "Information Systems",
    },
    description: {
      fr: "Mise en place d'une infrastructure SI d'entreprise complète : Active Directory, ERP Odoo et Plan de Reprise d'Activité.",
      en: "Setup of a complete enterprise IS infrastructure: Active Directory, Odoo ERP, and Disaster Recovery Plan.",
    },
    contributions: {
      fr: [
        "Configuration de VMs Windows Server pour héberger Active Directory",
        "Déploiement d'une VM Ubuntu pour accueillir l'ERP Odoo",
        "Participation à la mise en place du Plan de Reprise d'Activité (PRA)",
      ],
      en: [
        "Configured Windows Server VMs to host Active Directory",
        "Deployed an Ubuntu VM to run the Odoo ERP",
        "Participated in setting up the Disaster Recovery Plan (DRP)",
      ],
    },
    outcome: {
      fr: "Meilleure compréhension du fonctionnement d'une entreprise côté SI, notions de PRA et gestion de parc informatique.",
      en: "Better understanding of enterprise IS operations, DRP concepts and IT asset management.",
    },
    techs: [
      "Windows Server",
      "Active Directory",
      "Odoo",
      "Ubuntu",
      "VirtualBox",
    ],
    gradient: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
  },
  {
    id: 2,
    iconKey: "gl",
    title: {
      fr: "Génie Logiciel",
      en: "Software Engineering",
    },
    description: {
      fr: "Application de sauvegarde de données en C# .NET avec architecture MVVM et respect des bonnes pratiques de développement.",
      en: "Data backup application in C# .NET with MVVM architecture following software development best practices.",
    },
    contributions: {
      fr: [
        "Développement de l'interface utilisateur (WPF)",
        "Liaison entre les couches frontend et backend",
        "Mise en œuvre de l'architecture MVVM et des design patterns",
      ],
      en: [
        "Developed the WPF user interface",
        "Linked frontend and backend layers",
        "Implemented MVVM architecture and design patterns",
      ],
    },
    outcome: {
      fr: "Prise en main des design patterns, approfondissement de la POO et maîtrise de l'architecture MVVM en environnement .NET.",
      en: "Hands-on experience with design patterns, advanced OOP, and MVVM architecture mastery in .NET.",
    },
    techs: ["C#", ".NET", "WPF", "MVVM", "Visual Studio"],
    gradient: "linear-gradient(135deg, #7b2ff7 0%, #f107a3 100%)",
  },
  {
    id: 3,
    iconKey: "ro",
    title: {
      fr: "Recherche Opérationnelle",
      en: "Operational Research",
    },
    description: {
      fr: "Optimisation de tournées de livraison via l'algorithme de recuit simulé, avec visualisation animée des résultats en Python.",
      en: "Delivery route optimization via simulated annealing algorithm, with animated result visualization in Python.",
    },
    contributions: {
      fr: [
        "Participation active au développement et réglage de l'algorithme de recuit simulé",
        "Création d'un système de génération de GIFs pour visualiser les étapes de résolution",
      ],
      en: [
        "Actively participated in developing and tuning the simulated annealing algorithm",
        "Built a GIF generation system to visualize the algorithm's resolution steps",
      ],
    },
    outcome: {
      fr: "Première approche des problèmes NP-complexes et NP-difficiles, initiation à l'optimisation combinatoire.",
      en: "First exposure to NP-complex and NP-hard problems, introduction to combinatorial optimization.",
    },
    techs: ["Python", "Recuit simulé", "Algorithmes", "NP-Complet"],
    gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
  },
  {
    id: 4,
    iconKey: "web",
    title: {
      fr: "Web Avancé",
      en: "Advanced Web",
    },
    description: {
      fr: "Application sociale type Twitter avec abonnements, messagerie, posts, likes, commentaires et gestion de profil complète.",
      en: "Twitter-like social app with subscriptions, messaging, posts, likes, comments and full profile management.",
    },
    contributions: {
      fr: [
        "Développement backend avec Express.js et MongoDB",
        "Développement frontend des profils utilisateurs",
        "Implémentation du système d'abonnements et des posts",
      ],
      en: [
        "Backend development with Express.js and MongoDB",
        "Frontend development of user profiles",
        "Implemented the subscription and post system",
      ],
    },
    outcome: {
      fr: "Approfondissement des connaissances en développement web, maîtrise de React TypeScript et gestion de projet en équipe.",
      en: "Deepened web development skills, React TypeScript mastery and team project management.",
    },
    techs: ["React", "TypeScript", "Express.js", "MongoDB"],
    gradient: "linear-gradient(135deg, #f953c6 0%, #b91d73 100%)",
  },
  {
    id: 5,
    iconKey: "bd",
    title: {
      fr: "Big Data",
      en: "Big Data",
    },
    description: {
      fr: "Architecture Lakehouse pour la gestion de données hospitalières massives, avec pipelines ETL Python et dashboards Power BI.",
      en: "Lakehouse architecture for large-scale hospital data management, with Python ETL pipelines and Power BI dashboards.",
    },
    contributions: {
      fr: [
        "Développement d'un pipeline ETL en Python",
        "Création des dashboards de visualisation sur Power BI",
      ],
      en: [
        "Developed an ETL pipeline in Python",
        "Created data visualization dashboards in Power BI",
      ],
    },
    outcome: {
      fr: "Découverte du Big Data, maîtrise de Power BI et compréhension des architectures de données (Data Warehouse, Data Lake, Lakehouse).",
      en: "Big Data discovery, Power BI mastery, and understanding of data architectures (Data Warehouse, Data Lake, Lakehouse).",
    },
    techs: ["Python", "Power BI", "ETL", "Lakehouse", "SQL"],
    gradient: "linear-gradient(135deg, #0093E9 0%, #80D0C7 100%)",
  },
  {
    id: 6,
    iconKey: "iot",
    title: {
      fr: "IoT & Surveillance",
      en: "IoT & Monitoring",
    },
    description: {
      fr: "Boîtier de surveillance tout-en-un à base d'ESP32 et Raspberry Pi, avec dashboard React et déploiement CI/CD automatisé.",
      en: "All-in-one ESP32 & Raspberry Pi surveillance box with React dashboard and automated CI/CD deployment.",
    },
    contributions: {
      fr: [
        "Développement frontend du dashboard de surveillance",
        "Mise en place du pipeline CI/CD avec GitHub Actions",
        "Déploiement automatique sur Raspberry Pi via Tailscale (VPN)",
      ],
      en: [
        "Developed the surveillance dashboard frontend",
        "Set up the CI/CD pipeline with GitHub Actions",
        "Automated deployment to Raspberry Pi via Tailscale (VPN)",
      ],
    },
    outcome: {
      fr: "Découverte des notions IoT (BLE), consolidation des bases Git, maîtrise du CI/CD avec Docker et développement full stack embarqué.",
      en: "IoT concepts discovery (BLE), Git consolidation, CI/CD with Docker mastery, and embedded full stack development.",
    },
    techs: [
      "ESP32",
      "Raspberry Pi",
      "React Native",
      "Docker",
      "GitHub Actions",
      "BLE",
    ],
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  },
];

/* ── Project Card ── */
const ProjectCard = ({ project, index, t, lang, onClick }) => {
  const Icon = ICONS[project.iconKey];

  return (
    <motion.article
      className="project-card"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      onClick={onClick}
    >
      <div
        className="project-card__image"
        style={{ background: project.gradient }}
      >
        <div className="project-card__image-icon">
          <Icon size={52} color="rgba(255,255,255,0.9)" />
        </div>
        <div className="project-card__image-overlay">
          <span className="project-card__expand-hint">
            {t.projects.view_details} <FaChevronRight size={11} />
          </span>
        </div>
        <div className="project-card__number">0{project.id}</div>
      </div>

      <div className="project-card__body">
        <h3 className="project-card__title">{project.title[lang]}</h3>
        <p className="project-card__description">{project.description[lang]}</p>

        <div className="project-card__techs">
          {project.techs.map((tech) => (
            <span key={tech} className="project-card__tech">
              {tech}
            </span>
          ))}
        </div>

        <div className="project-card__links">
          <span className="project-card__link project-card__link--details">
            <FaChevronRight size={11} />
            {t.projects.view_details}
          </span>
          <span className="project-card__private">
            <FaLock size={10} />
            {t.projects.private_repo}
          </span>
        </div>
      </div>
    </motion.article>
  );
};

/* ── Project Modal ── */
const ProjectModal = ({ project, lang, t, onClose }) => {
  const Icon = ICONS[project.iconKey];

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <motion.div
      className="project-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="project-modal"
        initial={{ opacity: 0, scale: 0.93, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93, y: 30 }}
        transition={{ duration: 0.28, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient header */}
        <div
          className="project-modal__header"
          style={{ background: project.gradient }}
        >
          <div className="project-modal__header-icon">
            <Icon size={56} color="rgba(255,255,255,0.95)" />
          </div>
          <div className="project-modal__number">0{project.id}</div>
          <button
            className="project-modal__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <FaTimes size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="project-modal__body">
          <h2 className="project-modal__title">{project.title[lang]}</h2>
          <p className="project-modal__description">
            {project.description[lang]}
          </p>

          <div className="project-modal__techs">
            {project.techs.map((tech) => (
              <span key={tech} className="project-card__tech">
                {tech}
              </span>
            ))}
          </div>

          <div className="project-modal__section">
            <h4 className="project-modal__section-title">
              {t.projects.my_contributions}
            </h4>
            <ul className="project-modal__list">
              {project.contributions[lang].map((c, i) => (
                <li key={i} className="project-modal__list-item">
                  <FaCheckCircle className="project-modal__check" size={13} />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="project-modal__section">
            <h4 className="project-modal__section-title">
              {t.projects.outcome}
            </h4>
            <p className="project-modal__outcome">{project.outcome[lang]}</p>
          </div>

          <div className="project-modal__note">
            <FaLock size={11} />
            <span>{t.projects.private_note}</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ── Page ── */
const Projects = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    document.body.style.overflow = selectedProject ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedProject]);

  return (
    <>
      <motion.div
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <div className="projects-page">
          {/* Header */}
          <div className="projects-page__header">
            <div className="projects-page__header-bg" />
            <div className="projects-page__header-content">
              <motion.span
                className="section__tag"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                CESI Montpellier
              </motion.span>
              <motion.h1
                className="projects-page__title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                {t.projects.title}
              </motion.h1>
              <motion.p
                className="projects-page__subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 }}
              >
                {t.projects.subtitle}
              </motion.p>
            </div>
          </div>

          {/* Grid */}
          <div className="projects-grid__wrapper">
            <div className="projects-grid">
              {projects.map((project, i) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  index={i}
                  t={t}
                  lang={lang}
                  onClick={() => setSelectedProject(project)}
                />
              ))}
            </div>
          </div>
        </div>

        <footer className="footer">
          <div className="footer__container">
            <p>
              © {new Date().getFullYear()} Benjamin REPELLIN —{" "}
              {t.footer.made_with} React &nbsp;|&nbsp; {t.footer.rights}
            </p>
          </div>
        </footer>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            lang={lang}
            t={t}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Projects;
